import { useState, useRef, useEffect } from 'react'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { colorFor } from '../data/userColors.js'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * OwnerPicker — colored pill that opens a team-member dropdown on click.
 *
 * Renders the current owner's name tinted in their brand color (or a
 * yellow "TBD" state when unassigned). Clicking the pill opens a
 * scrollable list of team members plus an "Unassigned" option.
 * Selecting a name fires a useUpdateGoal mutation, so the change is
 * optimistic, undoable, and persists to the shared KV store.
 *
 * Props:
 *   pillarName    — pillar the goal lives in
 *   goalIndex     — index of the goal in the pillar
 *   subgoalIndex  — (optional) index of the sub-goal if this is for a sub-goal owner
 *   field         — 'owner' | 'dri' (which field on the goal/subgoal to update)
 *   value         — current value
 *   label         — (optional) prefix like "DRI:" or "Owner:"
 *   size          — 'sm' | 'md' (default md) — subgoals use 'sm' for the pill style
 */
export default function OwnerPicker({
  pillarName,
  goalIndex,
  subgoalIndex,
  field = 'owner',
  value,
  label,
  size = 'md',
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const mutation = useUpdateGoal()

  const isUnassigned = !value || value === '—' || value === 'TBD'
  const color = isUnassigned ? '#9ca3af' : colorFor(value)

  // Close the picker when clicking outside
  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // Slight delay so the click that opened it doesn't immediately close
    const t = setTimeout(() => {
      document.addEventListener('click', onClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const select = (name) => {
    const next = name === '—' ? '—' : name
    if (next !== value) {
      mutation.mutate({
        pillarName,
        goalIndex,
        subgoalIndex,
        field,
        value: next,
      })
    }
    setOpen(false)
  }

  // Subgoal-owner style: small colored pill, tight.
  // Goal DRI/Owner style: "Label: Name" pill with slightly more chrome.
  const pillStyle = size === 'sm'
    ? {
        fontSize: 10,
        fontWeight: 700,
        color,
        background: isUnassigned ? '#f3f4f6' : color + '18',
        border: `1px solid ${isUnassigned ? '#e5e7eb' : color + '30'}`,
        padding: '2px 8px',
        borderRadius: 99,
        cursor: 'pointer',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        justifySelf: 'start',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        userSelect: 'none',
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: isUnassigned ? '#fffbeb' : color + '18',
        color: isUnassigned ? '#92400e' : color,
        border: `1px solid ${isUnassigned ? '#fde68a' : color + '30'}`,
        borderRadius: 10,
        padding: '2px 8px',
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
        userSelect: 'none',
      }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((x) => !x)
        }}
        title={`Change ${field}`}
        style={{ ...pillStyle, border: `1px solid ${isUnassigned ? '#fde68a' : color + '30'}` }}
      >
        {label && <span style={{ opacity: 0.7, fontWeight: 500 }}>{label}:</span>}
        <span>{isUnassigned ? 'TBD' : value}</span>
        <span style={{ opacity: 0.55, fontSize: 8, marginLeft: 1 }}>▼</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 160,
            maxHeight: 280,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            padding: 6,
            zIndex: 950,
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              padding: '4px 8px 6px',
            }}
          >
            {field === 'dri' ? 'Assign DRI' : 'Assign owner'}
          </div>
          <button
            type="button"
            onClick={() => select('—')}
            style={{
              ...itemStyle,
              color: 'var(--muted)',
              fontStyle: 'italic',
              background: isUnassigned ? '#f9fafb' : 'transparent',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
            onMouseLeave={(e) => (e.currentTarget.style.background = isUnassigned ? '#f9fafb' : 'transparent')}
          >
            — Unassigned
          </button>
          {TEAM.map((name) => {
            const c = colorFor(name)
            const selected = name === value
            return (
              <button
                key={name}
                type="button"
                onClick={() => select(name)}
                style={{
                  ...itemStyle,
                  background: selected ? c + '12' : 'transparent',
                  fontWeight: selected ? 700 : 500,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = selected ? c + '18' : '#f9fafb')}
                onMouseLeave={(e) => (e.currentTarget.style.background = selected ? c + '12' : 'transparent')}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: c,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: '18px',
                    textAlign: 'center',
                  }}
                >
                  {name[0]}
                </span>
                {name}
                {selected && <span style={{ marginLeft: 'auto', color: c, fontSize: 10 }}>✓</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: 'none',
  padding: '6px 8px',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.1s',
}
